import React, { useMemo } from 'react';

import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import currencyFormatter from '~/utils/currencyFormatter';

import { computeSellVsMinOtaColor, computeSellVsMinOtaRange } from './otaParity/sellVsMinOtaHeatColor';
import { DailyDiscountStats, DailyMarketStats, ParsedOtaCsv } from './otaParity/types';

interface Props {
  parsed: ParsedOtaCsv;
  marketStats: Array<DailyMarketStats>;
  discountStats: Array<DailyDiscountStats>;
  currencyCode: string;
  maxHeight: number | string;
  /** Substring match against each row's date, e.g. from a parent-owned "Filter by date" input. */
  dateFilter?: string;
}

function dowLabel(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-AU', { weekday: 'short' });
}

function fmtPct(value: number | null): string {
  if (value == null) {
    return '—';
  }
  const rounded = Math.round(value);
  return rounded > 0 ? `+${rounded}%` : `${rounded}%`;
}

const stickyColumnSx = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 1,
  bgcolor: 'background.paper',
  whiteSpace: 'nowrap',
};

export default function ScrapeCsvTable({
  parsed,
  marketStats,
  discountStats,
  currencyCode,
  maxHeight,
  dateFilter,
}: Props) {
  const marketByDate = useMemo(() => new Map(marketStats.map((m) => [m.date, m])), [marketStats]);
  const discountByDate = useMemo(() => new Map(discountStats.map((d) => [d.date, d])), [discountStats]);

  const visibleRows = useMemo(
    () => (dateFilter ? parsed.rows.filter((r) => r.date.includes(dateFilter)) : parsed.rows),
    [parsed.rows, dateFilter],
  );

  // Uses the full dataset (unfiltered) so shading stays fixed regardless of the popup's date filter.
  const sellVsMinOtaRange = useMemo(() => {
    const values = discountStats.map((d) => d.sellVsMinOtaPct).filter((v): v is number => v != null);
    return computeSellVsMinOtaRange(values);
  }, [discountStats]);

  return (
    <Box>
      <TableContainer sx={{ maxHeight, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...stickyColumnSx, zIndex: 3, bgcolor: 'grey.100' }}>Date</TableCell>
              <TableCell sx={{ bgcolor: 'grey.100' }}>DOW</TableCell>
              <TableCell align="right" sx={{ bgcolor: 'grey.100', borderLeft: 2, borderColor: 'divider' }}>
                Low OTA
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: 'grey.100' }}>
                Sell vs Min OTA
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: 'grey.100' }}>
                Genuine Discount
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: 'grey.100' }}>
                Sell {currencyCode}
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: 'grey.100' }}>
                PRV + Min OTA
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: 'grey.100' }}>
                LE (Live Flash)
              </TableCell>
              {parsed.otaColumns.map((col) => (
                <TableCell key={col} align="right" sx={{ bgcolor: 'grey.100', whiteSpace: 'nowrap' }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => {
              const market = marketByDate.get(row.date);
              const discount = discountByDate.get(row.date);
              const dow = new Date(`${row.date}T00:00:00`).getDay();
              const isWeekend = dow === 0 || dow === 6;

              return (
                <TableRow key={row.date} sx={isWeekend ? { bgcolor: 'grey.50' } : undefined}>
                  <TableCell sx={stickyColumnSx}>{row.date}</TableCell>
                  <TableCell>{dowLabel(row.date)}</TableCell>
                  <TableCell align="right" sx={{ borderLeft: 2, borderColor: 'divider' }}>
                    {market?.low != null ? currencyFormatter(currencyCode, market.low, 0) : '—'}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={
                      discount?.sellVsMinOtaPct != null && sellVsMinOtaRange
                        ? computeSellVsMinOtaColor(discount.sellVsMinOtaPct, sellVsMinOtaRange)
                        : undefined
                    }
                  >
                    {fmtPct(discount?.sellVsMinOtaPct ?? null)}
                  </TableCell>
                  <TableCell align="right">{fmtPct(discount?.genDiscountPct ?? null)}</TableCell>
                  <TableCell align="right">
                    {discount?.sellDisplay != null ? currencyFormatter(currencyCode, discount.sellDisplay, 0) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {discount?.prvPlusMin != null ? currencyFormatter(currencyCode, discount.prvPlusMin, 0) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {row.leFlashRate != null ? currencyFormatter(currencyCode, row.leFlashRate, 0) : '—'}
                  </TableCell>
                  {parsed.otaColumns.map((col) => {
                    const value = row.otaRates[col];
                    return (
                      <TableCell key={col} align="right">
                        {value != null ? currencyFormatter(currencyCode, value, 0) : '—'}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
